import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  DollarSign, TrendingUp, TrendingDown, Building2, Shield,
  CreditCard, Handshake, BarChart3, ArrowUpRight, ArrowDownRight,
  Plus, Activity, Landmark, Package, Wallet, FileText
} from 'lucide-react';
import { authService } from '../services/auth';
import apiClient from '../services/api';
import { toast } from 'sonner';

export default function FundingDashboardPage() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showWizard, setShowWizard] = useState(false);
  const [wizardStep, setWizardStep] = useState(0);
  const [dashboard, setDashboard] = useState(null);
  const [cashflow, setCashflow] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [creditProfile, setCreditProfile] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const profileData = await authService.getProfile();
      setUser(profileData.member);

      const [dashRes, cashRes, facilRes, creditRes] = await Promise.allSettled([
        apiClient.get('/api/funding/dashboard'),
        apiClient.get('/api/funding/cashflow'),
        apiClient.get('/api/funding/facilities'),
        apiClient.get('/api/funding/credit-profile'),
      ]);

      setDashboard(dashRes.status === 'fulfilled' ? dashRes.value.data : {
        totalFunding: 2450000,
        activeLines: 3,
        supplyChainHealth: 87,
        monthlyBurn: 185000,
      });

      setCashflow(cashRes.status === 'fulfilled' ? cashRes.value.data?.timeline || [] : [
        { month: 'Oct', inflow: 320000, outflow: 185000 },
        { month: 'Nov', inflow: 380000, outflow: 210000 },
        { month: 'Dec', inflow: 450000, outflow: 280000 },
        { month: 'Jan', inflow: 340000, outflow: 190000 },
        { month: 'Feb', inflow: 395000, outflow: 205000 },
        { month: 'Mar', inflow: 410000, outflow: 195000 },
      ]);

      setFacilities(facilRes.status === 'fulfilled' ? facilRes.value.data?.facilities || [] : [
        { id: 1, name: 'Revolving Credit Line', provider: 'First National Bank', limit: 1000000, used: 620000, rate: '7.5%', status: 'active', maturity: '2027-06-15' },
        { id: 2, name: 'Invoice Factoring', provider: 'CapitalFlow', limit: 500000, used: 340000, rate: '4.2%', status: 'active', maturity: '2026-12-01' },
        { id: 3, name: 'Term Loan', provider: 'Commerce Bank', limit: 750000, used: 750000, rate: '6.8%', status: 'active', maturity: '2028-03-30' },
        { id: 4, name: 'Equipment Finance', provider: 'LeaseCo', limit: 200000, used: 0, rate: '5.1%', status: 'available', maturity: 'On demand' },
      ]);

      setCreditProfile(creditRes.status === 'fulfilled' ? creditRes.value.data : {
        score: 742,
        grade: 'A-',
        debtToEquity: 0.42,
        currentRatio: 2.1,
        interestCoverage: 4.8,
        paymentHistory: 'Excellent',
      });
    } catch (error) {
      console.error('Failed to load funding data:', error);
      if (error.response?.status === 401) navigate('/login');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    sessionStorage.clear();
    navigate('/login');
  };

  const handleRequestFunding = () => {
    setShowWizard(true);
    setWizardStep(0);
  };

  const handleWizardNext = () => {
    if (wizardStep < 2) {
      setWizardStep(wizardStep + 1);
    } else {
      toast.success('Funding request submitted', {
        description: 'Our team will review your application within 24 hours',
      });
      setShowWizard(false);
      setWizardStep(0);
    }
  };

  const maxCashflow = Math.max(...cashflow.map((d) => Math.max(d.inflow, d.outflow)));

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#0B1120' }}>
        <div className="text-center">
          <svg className="w-12 h-12 text-[#3EF1C7] animate-spin mx-auto mb-4" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p className="text-[#9CB0C7]">Loading funding dashboard...</p>
        </div>
      </div>
    );
  }

  const collaterals = [
    { name: 'Inventory', value: '$1.2M', status: 'verified', change: '+5%' },
    { name: 'Receivables', value: '$840K', status: 'verified', change: '+12%' },
    { name: 'Equipment', value: '$320K', status: 'pending', change: '0%' },
    { name: 'IP / Brand Value', value: '$450K', status: 'estimated', change: '+3%' },
  ];

  const negotiations = [
    { lender: 'Growth Capital Partners', type: 'Term Loan', amount: '$500K', status: 'term-sheet', lastUpdate: '2 days ago' },
    { lender: 'Venture Debt Co.', type: 'Revenue Loan', amount: '$300K', status: 'reviewing', lastUpdate: '5 days ago' },
    { lender: 'Trade Finance LLC', type: 'Supply Chain', amount: '$200K', status: 'approved', lastUpdate: '1 day ago' },
  ];

  const bondAnalysis = [
    { name: '90-Day Commercial Paper', yield: '5.2%', rating: 'A1', liquidity: 'High' },
    { name: '6-Month Treasury', yield: '4.8%', rating: 'AAA', liquidity: 'High' },
    { name: 'Corporate Bond Fund', yield: '6.1%', rating: 'BBB+', liquidity: 'Medium' },
  ];

  const getNegotiationColor = (status) => {
    const map = {
      'term-sheet': 'text-[#FFC857] bg-[#FFC857]/10',
      'reviewing': 'text-[#7A5CFF] bg-[#7A5CFF]/10',
      'approved': 'text-[#3EF1C7] bg-[#3EF1C7]/10',
      'rejected': 'text-[#F55454] bg-[#F55454]/10',
    };
    return map[status] || 'text-[#9CB0C7] bg-[#9CB0C7]/10';
  };

  return (
    <div className="min-h-screen" style={{ background: '#0B1120' }}>
      <header className="border-b border-[#22304A] bg-[#0E1426]/80 backdrop-blur">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">Funding Dashboard</h1>
            <p className="text-sm text-[#9CB0C7]">Funding & Liquidity</p>
          </div>
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/dashboard')} className="px-4 py-2 rounded-lg border border-[#22304A] bg-[#111A2E] text-white hover:bg-[#1A2437] transition-colors">
              ← Dashboard
            </button>
            <div className="text-right">
              <p className="text-sm text-white font-medium">{user?.name}</p>
              <p className="text-xs text-[#9CB0C7]">{user?.email}</p>
            </div>
            <button onClick={handleLogout} className="px-4 py-2 rounded-lg border border-[#22304A] bg-[#111A2E] text-white hover:bg-[#1A2437] transition-colors">
              Sign Out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Title + Request Funding */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <Landmark className="w-8 h-8 text-[#3EF1C7]" />
            <div>
              <h2 className="text-3xl font-bold text-white">Funding & Liquidity</h2>
              <p className="text-[#9CB0C7]">Cashflow, credit facilities, collateral, and lender negotiations</p>
            </div>
          </div>
          <button
            onClick={handleRequestFunding}
            className="flex items-center gap-2 px-6 py-3 rounded-lg bg-[#3EF1C7] text-[#0B1120] font-semibold hover:bg-[#35D9B2] transition-colors"
          >
            <Plus className="w-5 h-5" />
            Request Funding
          </button>
        </div>

        {/* Overview Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {[
            { label: 'Total Funding', value: `$${(dashboard?.totalFunding / 1000000).toFixed(1)}M`, icon: DollarSign, color: '#3EF1C7' },
            { label: 'Active Lines', value: dashboard?.activeLines, icon: Building2, color: '#7A5CFF' },
            { label: 'Supply Chain Health', value: `${dashboard?.supplyChainHealth}%`, icon: Package, color: '#FFC857' },
            { label: 'Monthly Burn', value: `$${(dashboard?.monthlyBurn / 1000).toFixed(0)}K`, icon: Wallet, color: '#F55454' },
          ].map((stat, idx) => {
            const Icon = stat.icon;
            return (
              <div key={idx} className="rounded-lg border border-[#22304A] bg-[#111A2E] p-5">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: `${stat.color}15` }}>
                    <Icon className="w-5 h-5" style={{ color: stat.color }} />
                  </div>
                </div>
                <p className="text-2xl font-bold text-white mb-1">{stat.value}</p>
                <p className="text-sm text-[#9CB0C7]">{stat.label}</p>
              </div>
            );
          })}
        </div>

        {/* Cashflow Chart */}
        <div className="rounded-lg border border-[#22304A] bg-[#111A2E] p-6 mb-8">
          <div className="flex items-center gap-2 mb-4">
            <BarChart3 className="w-5 h-5 text-[#7A5CFF]" />
            <h3 className="text-lg font-semibold text-white">Cashflow Overview</h3>
          </div>
          <div className="flex items-end gap-4 h-48">
            {cashflow.map((d, idx) => (
              <div key={idx} className="flex-1 flex flex-col items-center gap-1">
                <div className="w-full flex gap-1 items-end" style={{ height: '160px' }}>
                  <div
                    className="flex-1 rounded-t bg-[#3EF1C7]"
                    style={{ height: `${(d.inflow / maxCashflow) * 100}%` }}
                    title={`Inflow: $${(d.inflow / 1000).toFixed(0)}K`}
                  />
                  <div
                    className="flex-1 rounded-t bg-[#F55454]/70"
                    style={{ height: `${(d.outflow / maxCashflow) * 100}%` }}
                    title={`Outflow: $${(d.outflow / 1000).toFixed(0)}K`}
                  />
                </div>
                <span className="text-xs text-[#9CB0C7]">{d.month}</span>
              </div>
            ))}
          </div>
          <div className="flex items-center gap-6 mt-4">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded bg-[#3EF1C7]" />
              <span className="text-xs text-[#9CB0C7]">Inflow</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded bg-[#F55454]/70" />
              <span className="text-xs text-[#9CB0C7]">Outflow</span>
            </div>
          </div>
        </div>

        {/* Facilities + Credit Profile */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {/* Facilities */}
          <div className="lg:col-span-2 rounded-lg border border-[#22304A] bg-[#111A2E] p-6">
            <div className="flex items-center gap-2 mb-4">
              <Building2 className="w-5 h-5 text-[#7A5CFF]" />
              <h3 className="text-lg font-semibold text-white">Active Facilities</h3>
            </div>
            <div className="space-y-4">
              {facilities.map((facility) => {
                const utilization = facility.limit > 0 ? Math.round((facility.used / facility.limit) * 100) : 0;
                const utilColor = utilization > 80 ? '#F55454' : utilization > 50 ? '#FFC857' : '#3EF1C7';
                return (
                  <div key={facility.id} className="p-4 rounded-lg bg-[#0B1120] border border-[#22304A]">
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <p className="text-sm font-semibold text-white">{facility.name}</p>
                        <p className="text-xs text-[#9CB0C7]">{facility.provider}</p>
                      </div>
                      <span className={`text-xs font-semibold px-2 py-1 rounded ${
                        facility.status === 'active' ? 'text-[#3EF1C7] bg-[#3EF1C7]/10' : 'text-[#7A5CFF] bg-[#7A5CFF]/10'
                      }`}>
                        {facility.status}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-xs text-[#9CB0C7] mb-2">
                      <span>${(facility.used / 1000).toFixed(0)}K / ${(facility.limit / 1000).toFixed(0)}K</span>
                      <span>Rate: {facility.rate}</span>
                      <span>Maturity: {facility.maturity}</span>
                    </div>
                    <div className="w-full h-2 rounded-full bg-[#22304A]">
                      <div
                        className="h-full rounded-full transition-all"
                        style={{ width: `${utilization}%`, background: utilColor }}
                      />
                    </div>
                    <div className="mt-1 text-right">
                      <span className="text-xs font-semibold" style={{ color: utilColor }}>{utilization}% utilized</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Credit Profile */}
          <div className="rounded-lg border border-[#22304A] bg-[#111A2E] p-6">
            <div className="flex items-center gap-2 mb-4">
              <CreditCard className="w-5 h-5 text-[#3EF1C7]" />
              <h3 className="text-lg font-semibold text-white">Credit Profile</h3>
            </div>
            <div className="text-center mb-4">
              <span className="text-5xl font-bold text-[#3EF1C7]">{creditProfile?.score}</span>
              <span className="text-lg text-[#9CB0C7] ml-2">({creditProfile?.grade})</span>
            </div>
            <div className="space-y-3 text-sm">
              {[
                { label: 'Debt-to-Equity', value: creditProfile?.debtToEquity },
                { label: 'Current Ratio', value: creditProfile?.currentRatio },
                { label: 'Interest Coverage', value: `${creditProfile?.interestCoverage}x` },
                { label: 'Payment History', value: creditProfile?.paymentHistory },
              ].map((item, idx) => (
                <div key={idx} className="flex justify-between p-2 rounded bg-[#0B1120]">
                  <span className="text-[#9CB0C7]">{item.label}</span>
                  <span className="text-white font-semibold">{item.value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Collateral + Negotiations */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Collateral Status */}
          <div className="rounded-lg border border-[#22304A] bg-[#111A2E] p-6">
            <div className="flex items-center gap-2 mb-4">
              <Shield className="w-5 h-5 text-[#FFC857]" />
              <h3 className="text-lg font-semibold text-white">Collateral Status</h3>
            </div>
            <div className="space-y-3">
              {collaterals.map((col, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 rounded-lg bg-[#0B1120] border border-[#22304A]">
                  <div>
                    <p className="text-sm font-medium text-white">{col.name}</p>
                    <span className={`text-xs ${
                      col.status === 'verified' ? 'text-[#3EF1C7]' :
                      col.status === 'pending' ? 'text-[#FFC857]' : 'text-[#9CB0C7]'
                    }`}>{col.status}</span>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-semibold text-white">{col.value}</p>
                    <span className={`text-xs ${col.change.startsWith('+') ? 'text-[#3EF1C7]' : 'text-[#9CB0C7]'}`}>{col.change}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Lender Negotiations */}
          <div className="rounded-lg border border-[#22304A] bg-[#111A2E] p-6">
            <div className="flex items-center gap-2 mb-4">
              <Handshake className="w-5 h-5 text-[#7A5CFF]" />
              <h3 className="text-lg font-semibold text-white">Lender Negotiations</h3>
            </div>
            <div className="space-y-3">
              {negotiations.map((neg, idx) => (
                <div key={idx} className="p-3 rounded-lg bg-[#0B1120] border border-[#22304A]">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm font-medium text-white">{neg.lender}</p>
                    <span className={`text-xs font-semibold px-2 py-1 rounded ${getNegotiationColor(neg.status)}`}>
                      {neg.status.replace('-', ' ')}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-xs text-[#9CB0C7]">
                    <span>{neg.type}</span>
                    <span className="text-[#FFC857] font-semibold">{neg.amount}</span>
                    <span>{neg.lastUpdate}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Bond Analysis */}
        <div className="rounded-lg border border-[#22304A] bg-[#111A2E] p-6 mb-8">
          <div className="flex items-center gap-2 mb-4">
            <FileText className="w-5 h-5 text-[#3EF1C7]" />
            <h3 className="text-lg font-semibold text-white">Bond Analysis</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {bondAnalysis.map((bond, idx) => (
              <div key={idx} className="p-4 rounded-lg bg-[#0B1120] border border-[#22304A]">
                <p className="text-sm font-semibold text-white mb-2">{bond.name}</p>
                <div className="space-y-1 text-xs">
                  <div className="flex justify-between">
                    <span className="text-[#9CB0C7]">Yield</span>
                    <span className="text-[#3EF1C7] font-semibold">{bond.yield}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#9CB0C7]">Rating</span>
                    <span className="text-white font-semibold">{bond.rating}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#9CB0C7]">Liquidity</span>
                    <span className="text-white">{bond.liquidity}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Supply Chain Health */}
        <div className="rounded-lg border border-[#22304A] bg-[#111A2E] p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Activity className="w-5 h-5 text-[#FFC857]" />
              <h3 className="text-lg font-semibold text-white">Supply Chain Health Score</h3>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-48 h-3 rounded-full bg-[#22304A]">
                <div
                  className="h-full rounded-full bg-[#FFC857]"
                  style={{ width: `${dashboard?.supplyChainHealth || 0}%` }}
                />
              </div>
              <span className="text-lg font-bold text-[#FFC857]">{dashboard?.supplyChainHealth}%</span>
            </div>
          </div>
        </div>
      </main>

      {/* Funding Wizard Modal */}
      {showWizard && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-[#111A2E] border border-[#22304A] rounded-xl p-8 max-w-lg w-full mx-4">
            <h3 className="text-xl font-bold text-white mb-2">Request Funding</h3>
            <p className="text-sm text-[#9CB0C7] mb-6">Step {wizardStep + 1} of 3</p>

            {/* Step indicators */}
            <div className="flex gap-2 mb-6">
              {[0, 1, 2].map((step) => (
                <div
                  key={step}
                  className={`flex-1 h-1.5 rounded-full ${step <= wizardStep ? 'bg-[#3EF1C7]' : 'bg-[#22304A]'}`}
                />
              ))}
            </div>

            {wizardStep === 0 && (
              <div className="space-y-4">
                <label className="block">
                  <span className="text-sm text-[#9CB0C7]">Funding Type</span>
                  <select className="mt-1 w-full px-4 py-2 rounded-lg bg-[#0B1120] border border-[#22304A] text-white focus:outline-none focus:border-[#3EF1C7]">
                    <option>Revolving Credit</option>
                    <option>Term Loan</option>
                    <option>Invoice Factoring</option>
                    <option>Supply Chain Finance</option>
                  </select>
                </label>
                <label className="block">
                  <span className="text-sm text-[#9CB0C7]">Amount Requested</span>
                  <input type="text" placeholder="$250,000" className="mt-1 w-full px-4 py-2 rounded-lg bg-[#0B1120] border border-[#22304A] text-white placeholder-[#9CB0C7] focus:outline-none focus:border-[#3EF1C7]" />
                </label>
              </div>
            )}

            {wizardStep === 1 && (
              <div className="space-y-4">
                <label className="block">
                  <span className="text-sm text-[#9CB0C7]">Purpose of Funding</span>
                  <textarea rows="3" placeholder="Describe how the funds will be used..." className="mt-1 w-full px-4 py-2 rounded-lg bg-[#0B1120] border border-[#22304A] text-white placeholder-[#9CB0C7] focus:outline-none focus:border-[#3EF1C7]" />
                </label>
                <label className="block">
                  <span className="text-sm text-[#9CB0C7]">Preferred Term</span>
                  <select className="mt-1 w-full px-4 py-2 rounded-lg bg-[#0B1120] border border-[#22304A] text-white focus:outline-none focus:border-[#3EF1C7]">
                    <option>6 months</option>
                    <option>12 months</option>
                    <option>24 months</option>
                    <option>36 months</option>
                  </select>
                </label>
              </div>
            )}

            {wizardStep === 2 && (
              <div className="space-y-4">
                <div className="p-4 rounded-lg bg-[#0B1120] border border-[#22304A]">
                  <p className="text-sm text-[#9CB0C7] mb-2">Review your request</p>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between"><span className="text-[#9CB0C7]">Credit Score</span><span className="text-[#3EF1C7] font-semibold">{creditProfile?.score}</span></div>
                    <div className="flex justify-between"><span className="text-[#9CB0C7]">Grade</span><span className="text-white font-semibold">{creditProfile?.grade}</span></div>
                    <div className="flex justify-between"><span className="text-[#9CB0C7]">Active Facilities</span><span className="text-white font-semibold">{dashboard?.activeLines}</span></div>
                  </div>
                </div>
                <p className="text-xs text-[#9CB0C7]">By submitting, you authorize Commerce Index to share your financial data with potential lenders.</p>
              </div>
            )}

            <div className="flex justify-between mt-6">
              <button
                onClick={() => wizardStep > 0 ? setWizardStep(wizardStep - 1) : setShowWizard(false)}
                className="px-4 py-2 rounded-lg border border-[#22304A] text-[#9CB0C7] hover:text-white transition-colors"
              >
                {wizardStep > 0 ? 'Back' : 'Cancel'}
              </button>
              <button
                onClick={handleWizardNext}
                className="px-6 py-2 rounded-lg bg-[#3EF1C7] text-[#0B1120] font-semibold hover:bg-[#35D9B2] transition-colors"
              >
                {wizardStep < 2 ? 'Next' : 'Submit Request'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
